import React, { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { staggerRevealOnScroll } from '../../utils/animations';
import styles from './Services.module.scss';

const servicesList = [
  {
    title: 'Search Engine Optimization (SEO)',
    description: 'Data-driven on-page, off-page, and technical SEO strategies to dominate search engine results and capture high-intent organic traffic.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"></circle>
        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        <line x1="11" y1="8" x2="11" y2="14"></line>
        <line x1="8" y1="11" x2="14" y2="11"></line>
      </svg>
    )
  },
  {
    title: 'Digital Marketing Campaigns',
    description: 'High-performing, end-to-end campaigns designed to build brand authority, generate quality leads, and accelerate business growth.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"></path>
      </svg>
    )
  },
  {
    title: 'Social Media Strategy & Marketing',
    description: 'Strategic management and advertising campaigns across platforms to build active communities and engage target audiences.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
      </svg>
    )
  },
  {
    title: 'Video Shoot & Professional Editing',
    description: 'Compelling high-quality video production and editing services to tell your brand story and capture user attention.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 7l-7 5 7 5V7z"></path>
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
      </svg>
    )
  },
  {
    title: 'Premium Graphic Design',
    description: 'Bespoke branding assets, marketing collateral, and creative visuals tailored to elevate your digital and social media presence.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"></path>
        <path d="M12 16C14.2091 16 16 14.2091 16 12C16 9.79086 14.2091 8 12 8C9.79086 8 8 9.79086 8 12C8 14.2091 9.79086 16 12 16Z"></path>
      </svg>
    )
  },
  {
    title: 'CRM Support & Systems Automation',
    description: 'Optimize customer relationships and sales pipelines through smart tool setup, tracking systems, and automation.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
        <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
        <line x1="6" y1="6" x2="6.01" y2="6"></line>
        <line x1="6" y1="18" x2="6.01" y2="18"></line>
      </svg>
    )
  }
];

const Services: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Rely on pure CSS stagger animations for rendering reliability
  }, []);

  return (
    <section className={styles.servicesSection} id="services">
      <div ref={containerRef} className={styles.container}>
        <div className={styles.header}>
          <h2 className={styles.sectionTitle}>Digital <span className={styles.highlight}>Expertise</span></h2>
          <p className={styles.sectionSubtitle}>
            Specialized solutions to elevate your digital presence.
          </p>
        </div>

        <div className={styles.grid}>
          {servicesList.map((service, index) => (
            <div key={index} className={styles.serviceCard}>
              <div className={styles.cardInner}>
                <div className={styles.cardTop}>
                  <div className={styles.iconContainer}>{service.icon}</div>
                  <div className={styles.cardNumber}>0{index + 1}</div>
                </div>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
              <div className={styles.cardHoverEffect}></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
